import React from 'react';
import { useDispatch } from 'react-redux';
import { IconButton } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { Icon, useIsModuleDisabled, useSettings } from 'app/main/components';
import { ViewTableUtils } from 'app/main/components/page-layouts';
import { showContractsLinesEditDialog } from './ContractsLinesEditDialog';

type useContractsLinesTableColumnsType = (params: { updateRefresh: VoidFunction }) => GridColDef[];

function EditButton({ row, updateRefresh }: { row: any; updateRefresh: VoidFunction }) {
	const dispatch = useDispatch();

	const onClick = () => dispatch(showContractsLinesEditDialog({ baseModel: row, updateRefresh }));

	return (
		<IconButton size="small" onClick={onClick}>
			<Icon icon="edit" />
		</IconButton>
	);
}

const useContractsLinesTableColumns: useContractsLinesTableColumnsType = ({ updateRefresh }) => {
	const { t } = useSettings();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);

	const columns: GridColDef[] = [
		{
			field: 'cnli_d_billing',
			headerName: t('DETAILS_CONTRACTS_LINES_FIELD_BILLING_DATE'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'cnli_amount',
			type: 'number',
			headerName: t('DETAILS_CONTRACTS_LINES_FIELD_AMOUNT'),
			minWidth: 150,
			renderCell: ViewTableUtils.renderCurrency,
			hideable: false
		},
		{
			field: 'cnli_description',
			headerName: t('DETAILS_CONTRACTS_LINES_FIELD_DESCRIPTION'),
			minWidth: 200,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip
		}
	];

	if (!isModuleDisabled('invoices') && authorizer.can('view', 'invoices')) {
		columns.push({
			field: 'invc_title',
			headerName: t('DETAILS_CONTRACTS_LINES_FIELD_INVOICE'),
			minWidth: 150,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'invoices', uid: row.invc_uid } })
		});
	}

	columns.push({
		field: 'cnli_d_billed_final',
		headerName: t('DETAILS_CONTRACTS_LINES_FIELD_DATE_BILLED'),
		minWidth: 150,
		renderCell: ViewTableUtils.renderDate
	});

	columns.push({
		type: 'actions',
		field: 'actions',
		width: 40,
		renderCell: ({ row }) => <EditButton row={row} updateRefresh={updateRefresh} />,
		sortable: false
	});

	return columns;
};

export default useContractsLinesTableColumns;
